// ghostMath — v0.328.0 (2026-11-04)
// ---------------------------------------------------------------------------
// The pure parts of the GHOST CAMERA (components/GhostCamera.jsx), kept out of
// React so node:test can hold them. Ported from InSpec's field app (the same
// camera, the same rules), plus the DropPin rule for which photo it lays over.
//
// The camera frame takes the ORIGINAL photo's shape, and the original and the
// live picture both fill that frame the way CSS object-fit: cover does. The
// shot is then cut to exactly what was on screen, so the after photo has the
// before photo's shape and framing - the pair lines up side by side on the
// card, the client's page and the PDF.

/** The largest w x h box of aspect `aspect` (w/h) that fits inside maxW x maxH. */
export function fitBox(aspect, maxW, maxH) {
  const a = aspect > 0 && Number.isFinite(aspect) ? aspect : 3 / 4;
  if (!(maxW > 0) || !(maxH > 0)) return { w: 0, h: 0 };
  let w = maxW;
  let h = w / a;
  if (h > maxH) { h = maxH; w = h * a; }
  return { w: Math.round(w), h: Math.round(h) };
}

/** The source rectangle object-fit: cover shows when a srcW x srcH picture
 *  fills a box of aspect `boxAspect` - centred, the overflow cut evenly. */
export function coverCrop(srcW, srcH, boxAspect) {
  if (!(srcW > 0) || !(srcH > 0)) return { sx: 0, sy: 0, sw: 0, sh: 0 };
  const a = boxAspect > 0 && Number.isFinite(boxAspect) ? boxAspect : srcW / srcH;
  const srcAspect = srcW / srcH;
  if (srcAspect > a) {
    // source is wider than the box: full height, sides cut
    const sw = Math.round(srcH * a);
    return { sx: Math.round((srcW - sw) / 2), sy: 0, sw, sh: srcH };
  }
  const sh = Math.round(srcW / a);
  return { sx: 0, sy: Math.round((srcH - sh) / 2), sw: srcW, sh };
}

/**
 * The photo the ghost camera lays over: the item's FIRST saved photo that is
 * not itself an after - the photo it was raised with (the server picks the
 * same one for a subbie, api/contact-defects). It must be saved (an id the
 * after can name) and drawable (a url). Null = no ghost: take an ordinary photo.
 */
export function ghostBefore(photos) {
  const list = Array.isArray(photos) ? photos : [];
  return list.find((p) => p && p.id != null && !p.pending && !p.file
    && (p.displayUrl || p.url) && String(p.kind || '').toLowerCase() !== 'after') || null;
}

/** The address to draw a photo by: the mid-size copy when there is one. */
export function ghostSrc(photo) {
  return photo ? (photo.displayUrl || photo.url || null) : null;
}
